import { AppMeta } from "@xnav/types/src/common";
import { MediaMeta, MediaType } from "@/pages/mediaManager";
import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useProblem, useS3Client } from "@/hooks/contextHooks";
import { GetObjectCommand, HeadObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { AppContext } from "@/contexts/appContext";
import { MediaContext } from "@/contexts/mediaContext";
import { useAuthenticator } from "@aws-amplify/ui-react";
import Loading from "@/components/loading";
import { apiGetRequest } from "@/functions/requests";

function getMediaPath(appId: string, mediaId: string) {
    return `${appId}/media/media/${mediaId}`
}

function getIconPath(appId: string, iconId: string) {
    return `${appId}/media/icons/${iconId}`
}

export default function AppLayout() {
	const { appId } = useParams() as { appId: string }

	useEffect(() => {
		if (appId) {
			const openedStr = localStorage.getItem('recentlyOpenedCourses');
			let opened: string[] = [];
			try {
				opened = openedStr ? JSON.parse(openedStr) : [];
			} catch (e) {
				opened = [];
			}
			opened = opened.filter(id => id !== appId);
			opened.unshift(appId);
			opened = opened.slice(0, 20);
			localStorage.setItem('recentlyOpenedCourses', JSON.stringify(opened));
		}
	}, [appId]);

	const [, setProblem] = useProblem()

	const [app, setApp] = useState<AppMeta>()
	const [media, setMedia] = useState<MediaMeta[]>()
	useEffect(() => { console.log("Media update", media) }, [media])
	const s3 = useS3Client() as S3Client
	const { user } = useAuthenticator()

	async function fetchApp() {
		setApp(await apiGetRequest<string, AppMeta>(`/apps/${appId}`))
	}

	async function fetchMedia() {
		if (!app) return
		try {
            let tempMedia: MediaMeta[] = []
            try {
                const metaCommand = new GetObjectCommand({
                    Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                    Key: `${app.id}/media/meta.json`
                })
                const metaUrl = await getSignedUrl(s3 as any, metaCommand as any, { expiresIn: 360 })
                const response = await fetch(metaUrl)
                if (response.ok) {
                    const strData = await response.text()
                    tempMedia = JSON.parse(strData)
                }
            } catch (e) {
                console.log("S3 direct fetch failed, trying API", e)
            }
            if (!tempMedia || tempMedia.length === 0) {
                tempMedia = await apiGetRequest<string, MediaMeta[]>(`/media/${appId}`)
            }
            setMedia(tempMedia)
            const iconMediaPromises = tempMedia.map(async (media) => {
                const getMediaFileCmd = new GetObjectCommand({
                    Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                    Key: getMediaPath(app.id, media.id)
                })
                const fileUrl = await getSignedUrl(s3 as any, getMediaFileCmd as any, { expiresIn: 3600 })
                if (media.type === MediaType.Pdf || media.type === MediaType.Audio || !media.iconUrl) return {
                    ...media,
                    fileUrl: fileUrl
                }
                const getMediaIconCmd = new GetObjectCommand({
                    Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
                    Key: getIconPath(app.id, media.iconId)
                })
                const iconUrl = await getSignedUrl(s3 as any, getMediaIconCmd as any, { expiresIn: 3600 })
                return {
                    ...media,
                    iconUrl: iconUrl,
                    fileUrl: fileUrl
                }
            })
            const iconMedia = await Promise.all(iconMediaPromises)
            setMedia(iconMedia)
		} catch (e: any) {
			if (e.name === 'NoSuchKey') setMedia([])
			else setProblem(e)
		}
	}

	useEffect(() => {
		fetchApp()
	}, [])

	useEffect(() => {
		if (app && !media) fetchMedia()
	}, [app, media])

	if (!app || !media) return <Loading message='Loading app' />
	return (
		<AppContext.Provider value={app}>
			<MediaContext.Provider value={[media, setMedia]}>
				<Outlet />
			</MediaContext.Provider>
		</AppContext.Provider>
	)
}
